import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "./ui/card";
import {
  Globe,
  Phone,
  Check,
  Loader,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import confetti from "canvas-confetti";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";
import { useAuth } from "../hooks/useAuth";
import insurancePolicyService from "../lib/InsurancePolicyService";

const ViuDigitalQuoteScreen = ({ buyerData, onComplete, onBack }) => {
  const { trackButtonClick } = useInteractionTracking();
  const { getIdToken } = useAuth();
  const headerRef = useRef(null);

  // "loading" | "ready" | "error" | "profile_incomplete"
  const [status, setStatus] = useState("loading");
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const fireConfetti = () => {
    if (!headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { x, y },
      colors: ["#7B61FF", "#D4F6C9", "#CEC5FF", "#FFD700", "#A1CE90"],
    });
  };

  // Fetch the quote URL on mount — does NOT auto-open
  const fetchQuoteUrl = async () => {
    setStatus("loading");
    setErrorMessage("");

    try {
      const token = await getIdToken();
      if (!token) {
        setErrorMessage("Authentication required. Please try again.");
        setStatus("error");
        return;
      }

      const url = await insurancePolicyService.getViuRedirectUrl(token);

      if (!url) {
        setErrorMessage("Something went wrong. Please try again.");
        setStatus("error");
        return;
      }

      setRedirectUrl(url);
      setStatus("ready");
      fireConfetti();

      analyticsService.trackPageView("viu_digital_quote_ready", {
        buyer: buyerData?.name,
      });
    } catch (error) {
      console.error("Error fetching VIU quote URL:", error);
      setErrorMessage("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  // User taps button to open the quote
  const handleOpenQuote = async () => {
    console.log("open quote = " + redirectUrl);
    trackButtonClick("viu-open-digital-quote", {
      step_name: "viu_digital_quote_screen",
    });
    window.open(redirectUrl, "_blank");    
  };

  useEffect(() => {
    analyticsService.trackPageView("viu_digital_quote_screen", {
      buyer: buyerData?.name,
    });
    fetchQuoteUrl();
  }, []);

  // Loading state
  if (status === "loading") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4" ref={headerRef}>
          <div className="w-20 h-20 bg-[#F5F2FF] rounded-full flex items-center justify-center mx-auto">
            <Loader className="w-10 h-10 text-[#7B61FF] animate-spin" />
          </div>
          <h1 className="text-xl font-bold uppercase">
            Getting Your Quote Ready...
          </h1>
          <p className="text-darkGrey">
            We're preparing your personalized insurance quote with{" "}
            <span style={{ color: "#7A5CFA" }}>{buyerData?.name}</span>.
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4" ref={headerRef}>
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-xl font-bold uppercase">Something Went Wrong</h1>
          <p className="text-darkGrey">{errorMessage}</p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => {
              trackButtonClick("viu-digital-quote-retry", {
                step_name: "viu_digital_quote_screen",
              });
              fetchQuoteUrl();
            }}
            className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
          >
            Try Again
          </button>

          <button
            onClick={() => {
              trackButtonClick("viu-fallback-to-agent", {
                step_name: "viu_digital_quote_screen",
              });
              onBack();
            }}
            className="w-full flex items-center justify-center gap-2 uppercase font-bold border-2 border-[#7B61FF] text-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-[#F5F2FF] transition-colors"
          >
            <Phone className="w-5 h-5" />
            <span>Talk to an Agent Instead</span>
          </button>
        </div>
      </div>
    );
  }

  // Profile incomplete state
  if (status === "profile_incomplete") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4" ref={headerRef}>
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-xl font-bold uppercase">
            Profile Update Needed
          </h1>
          <p className="text-darkGrey">
            {errorMessage ||
              "Please update your profile with your name, email, and phone number before completing your quote."}
          </p>
        </div>

        <button
          onClick={() => {
            trackButtonClick("viu-back-to-choice", {
              step_name: "viu_digital_quote_screen",
            });
            onBack();
          }}
          className="w-full flex items-center justify-center gap-2 uppercase font-bold border-2 border-[#7B61FF] text-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-[#F5F2FF] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Ready state — URL fetched, user taps to open
  return (
    <div className="space-y-6">
      <div className="text-center space-y-4" ref={headerRef}>
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-xl font-bold">You're almost there!</h1>
        <p className="text-darkGrey">
          <span style={{ color: "#7A5CFA" }}>{buyerData?.name}</span> is here to
          help you compare quotes in seconds with customized coverage
        </p>
      </div>

      {/* Buyer Logo */}
      {buyerData?.logo && (
        <div className="flex justify-center">
          <div className="bg-gray-50 p-4 rounded-lg">
            <img
              src={buyerData.logo}
              alt={buyerData.name}
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      )}

      {/* Primary CTA — user taps to open quote */}
      <button
        onClick={handleOpenQuote}
        className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-4 rounded-[10px] hover:bg-purpleGradientDark transition-colors text-lg shadow-lg shadow-[#7B61FF]/30"
      >
        <ExternalLink className="w-5 h-5" />
        <span>Get Quotes</span>
      </button>

      {/* What to Expect */}
      <Card className="border-gray-200 rounded-[20px]">
        <CardContent className="p-5">
          <h3 className="font-bold text-gray-900 mb-3">What to Expect:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Complete your quote on the {buyerData?.name} website</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Compare personalized insurance options</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* <button
        onClick={() => {
          trackButtonClick("viu-digital-quote-continue", {
            step_name: "viu_digital_quote_screen",
          });
          onComplete();
        }}
        className="w-full flex items-center justify-center gap-2 uppercase font-bold border-2 border-[#7B61FF] text-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-[#F5F2FF] transition-colors"
      >
        Continue
      </button> */}
    </div>
  );
};

export default ViuDigitalQuoteScreen;
